import {
  HistoryMessageItem,
  AnyMessageCallEvent,
  MessageStatusType,
  useIM,
  isCallPastMessage,
  CallPastMessage,
  isCallHistoryEvent,
  RequestChatHistoryPayload,
} from '@wui/im'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'

import { useStoreLastReadEventIds } from '@/entities/room'
import { useNotification } from '@/entities/use-notification'
import { CallMediaRequest  } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import {
  isCallTimestampWithinLimitedWindow,
  shouldLimitCallHistoryDepth,
} from '@/shared/composables/user-features'
import { useIMStore } from '@/shared/store/im'
import { CALL_CONTACT_URI } from '@/shared/utils/constants'
import { insertAtStore } from '@/shared/utils/insert-at-store'
import { isPServedIsUser } from '@/shared/utils/uri'

import { getCallHistoryRank, isIncomingCall, isOutgoingPrivateCall, getCallHistoryPServed } from './lib'
import { ActiveConferenceParticipant, CallEventWithStatus } from './types'

type ActiveConferenceParticipantsByRoom = Map<string, Map<string, ActiveConferenceParticipant>>

const isConferenceParticipantCallMessage = (
  message: AnyMessageCallEvent,
): message is AnyMessageCallEvent & { roomName: string, participantPServed: string } => {
  return 'roomName' in message && 'participantPServed' in message && !!message.participantPServed
}

export const useCallsStore = defineStore('calls', () => {
  const callEvents = ref<Array<CallEventWithStatus>>([])
  const _allReceivedCallContactHistoryEvents = ref<HistoryMessageItem[]>([])
  const callMediaRequests = ref(new Map<string, CallMediaRequest>())
  const isCallsLoading = ref(false)
  const _isLastLoaded = ref(false)
  const isFirstFetchDone = ref(false)
  const activeConferenceParticipantsByRoom = shallowRef<ActiveConferenceParticipantsByRoom>(new Map())

  const { currentUser } = useCurrentUser()
  const { onCallMessage } = useIM()

  const setActiveConferenceParticipants = (roomPServed: string, participants: Map<string, ActiveConferenceParticipant>) => {
    const next = new Map(activeConferenceParticipantsByRoom.value)

    if (participants.size === 0) {
      next.delete(roomPServed)
    } else {
      next.set(roomPServed, participants)
    }

    activeConferenceParticipantsByRoom.value = next
  }

  const addActiveConferenceParticipant = (
    roomPServed: string,
    participantPServed: string,
    participantName: string | undefined,
    connectedAt: number,
  ) => {
    const participants = new Map(activeConferenceParticipantsByRoom.value.get(roomPServed) ?? [])
    const participant: ActiveConferenceParticipant = {
      participantPServed,
      connectedAt,
    }

    if (participantName) {
      participant.participantName = participantName
    }

    participants.set(participantPServed, participant)
    setActiveConferenceParticipants(roomPServed, participants)
  }

  const removeActiveConferenceParticipant = (roomPServed: string, participantPServed: string) => {
    const participants = new Map(activeConferenceParticipantsByRoom.value.get(roomPServed) ?? [])
    participants.delete(participantPServed)
    setActiveConferenceParticipants(roomPServed, participants)
  }

  const getActiveConferenceParticipants = (roomPServed: string): ActiveConferenceParticipant[] => {
    const participants = activeConferenceParticipantsByRoom.value.get(roomPServed)

    return participants ? [...participants.values()] : []
  }

  const getActiveConferenceParticipant = (
    roomPServed: string,
    participantPServed: string,
  ): ActiveConferenceParticipant | undefined => {
    return activeConferenceParticipantsByRoom.value.get(roomPServed)?.get(participantPServed)
  }

  const _addEvent = (event: CallPastMessage) => {
    if (!event.eventId) {
      console.warn(`Receive call message with undefined eventId ${JSON.stringify(event)}`)
      return
    }

    const eventWithStatus: CallEventWithStatus = {
      ...event,
      status: MessageStatusType.INITIAL,
      fakeRank: -event.eventId,
    }
    if (isOutgoingPrivateCall(event)) {
      eventWithStatus.status = MessageStatusType.READ
    }

    insertAtStore(callEvents.value, eventWithStatus, 'fakeRank')
  }

  const updateEventStatus = (eventId: number, status: MessageStatusType) => {
    const event = callEvents.value.find(e => e.eventId === eventId)

    if (event) {
      event.status = status
    }
  }

  const unreadCount = computed(() => {
    return callEvents.value.filter(e => {
      if (!isIncomingCall(e) && !(e.event === 'call_ended' && 'roomName' in e)) {
        return false
      }

      // Без callSourceRank - только старые сообщения, не учитываем их прочитанность
      if (!('callSourceRank' in e) || e.callSourceRank == null) {
        return false
      }

      const { getOwnMessageRankByRoomAndContactId } = useStoreLastReadEventIds()
      const pServed = getCallHistoryPServed(e)
      const lastReadMessageRank = getOwnMessageRankByRoomAndContactId(pServed)
      if (lastReadMessageRank === undefined || (!isPServedIsUser(pServed) && lastReadMessageRank === 0)) {
        return false
      }

      return lastReadMessageRank < getCallHistoryRank(e)
    }).length
  })

  const setCallMediaRequest = (callId: string, request: CallMediaRequest) => {
    callMediaRequests.value.set(callId, request)
  }

  const removeCallMediaRequest = (callId: string) => {
    callMediaRequests.value.delete(callId)
  }

  onCallMessage((message: AnyMessageCallEvent) => {
    if (message.event === 'appointment_start') {
      setActiveConferenceParticipants(message.pServed, new Map())
    } else if (message.event === 'appointment_end') {
      const next = new Map(activeConferenceParticipantsByRoom.value)
      next.delete(message.pServed)
      activeConferenceParticipantsByRoom.value = next
    } else if (isConferenceParticipantCallMessage(message)) {
      if (message.event === 'call_answered') {
        const participantName = 'participantName' in message ? message.participantName : undefined

        addActiveConferenceParticipant(
          message.pServed,
          message.participantPServed,
          participantName,
          message.timestamp,
        )
      } else if (message.event === 'call_ended') {
        removeActiveConferenceParticipant(message.pServed, message.participantPServed)
      }
    }

    if (message.from === CALL_CONTACT_URI) {
      return
    }

    const { eventId, timestamp, rank, event } = message
    const historyItem: HistoryMessageItem = { item: message, eventId, timestamp, rank, status: MessageStatusType.INITIAL, type: 0 }
    const isNotFromCurrentUser = currentUser?.value?.imLogin !== historyItem.item.pServed

    if (isIncomingCall(message) && isNotFromCurrentUser && message.from !== CALL_CONTACT_URI) {
      if (
        (event === 'call_missed')
        || (event === 'dialing_terminated')
      ) {
        useNotification().createCallMissedNotification(message)
      }
    }

    if (isCallHistoryEvent(historyItem)) {
      const { item } = historyItem
      if (
        item.event === 'call_ended' &&
        'roomName' in item &&
        'participantPServed' in item &&
        item.participantPServed !== currentUser.value?.imLogin
      ) {
        return
      }

      _addEvent({
        ...item,
        callSourceEventId: item.eventId,
        callSourceRank: item.rank,
        callSourceRemoteId: item.pServed,
      } as CallPastMessage)
    }
  })

  const fetchNext = async (): Promise<HistoryMessageItem[]> => {
    if (!currentUser.value?.imLogin || _isLastLoaded.value) {
      return []
    }

    try {
      isCallsLoading.value = true

      const payload: RequestChatHistoryPayload = {
        messagesLimit: 100,
        type: 'older',
        pServed: CALL_CONTACT_URI,
      }
      const last = _allReceivedCallContactHistoryEvents.value.at(-1)
      if (last) {
        payload.cursorEventId = last.eventId
      }

      const { events } = await useIM().requestChatHistory(payload)
      if (events.length === 0 || (events.length === 1 && events[0]?.eventId === last?.eventId)) {
        _isLastLoaded.value = true

        return []
      }

      _allReceivedCallContactHistoryEvents.value.push(...events)

      const limitDepth = shouldLimitCallHistoryDepth()
      events
        .filter(isCallHistoryEvent)
        .forEach((event) => {
          if (limitDepth && !isCallTimestampWithinLimitedWindow(event.timestamp)) {
            return
          }
          _addEvent(event.item)
        })

      if (
        limitDepth &&
        events.some((event) => !isCallTimestampWithinLimitedWindow(event.timestamp))
      ) {
        _isLastLoaded.value = true
      }

      return events
    } catch (error) {
      console.error(error)
      return []
    } finally {
      isCallsLoading.value = false
    }
  }
  const addEvents = (events: HistoryMessageItem[]) => {
    events
      .filter(isCallPastMessage)
      .forEach((message: HistoryMessageItem<CallPastMessage>) => {
        _addEvent(message.item)
      })
  }

  const { userImIsReady } = storeToRefs(useIMStore())

  watch(userImIsReady, (isReady) => {
    if (!isReady || isFirstFetchDone.value || !currentUser.value?.imLogin) {
      return
    }

    isFirstFetchDone.value = true
    fetchNext()
  }, { immediate: true })

  const isCallsHistoryFullyLoaded = computed(() => _isLastLoaded.value)

  const $reset = () => {
    callEvents.value = []
    _allReceivedCallContactHistoryEvents.value = []
    callMediaRequests.value = new Map()
    isCallsLoading.value = false
    _isLastLoaded.value = false
    isFirstFetchDone.value = false
    activeConferenceParticipantsByRoom.value = new Map()
  }

  return {
    callEvents,
    callMediaRequests,
    activeConferenceParticipantsByRoom,
    addEvents,
    unreadCount,
    updateEventStatus,
    setCallMediaRequest,
    removeCallMediaRequest,
    fetchNext,
    isCallsLoading,
    isCallsHistoryFullyLoaded,
    isFirstFetchDone,
    getActiveConferenceParticipants,
    getActiveConferenceParticipant,
    $reset,
  }
})
